import React, { useLayoutEffect, forwardRef, ReactElement, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ISubHeaderProps } from '../SubHeader/SubHeader';
import { IPageProps } from '../Page/Page';
import { useNavigate } from 'react-router-dom';
import { demoPagesMenu } from '../../menu';
import { useAppDispatch, useAppSelector } from '../../custom/redux/reduxHooks';
import authAPI from '../../custom/API/authAPI';
import { loginUser, logoutUser } from '../../custom/redux/reducers/authReducer';
import useDarkMode from '../../hooks/useDarkMode';
import { toast } from 'react-toastify';
import { handleApiError } from '../../helpers/apiErrorHandler';
import { isAdminRole } from '../../helpers/adminRoles';

interface IPageWrapperProps {
	isProtected?: boolean;
	title?: string;
	description?: string;
	children:
		| ReactElement<ISubHeaderProps>[]
		| ReactElement<IPageProps>
		| ReactElement<IPageProps>[];
	className?: string;
}
const PageWrapper = forwardRef<HTMLDivElement, IPageWrapperProps>(
	({ isProtected, title, description, className, children }, ref) => {
		const { darkModeStatus } = useDarkMode();
		useLayoutEffect(() => {
			// @ts-ignore
			document.getElementsByTagName('TITLE')[0].text = `${title ? `${title} | ` : ''}${
				process.env.REACT_APP_SITE_NAME
			}`;
			// @ts-ignore
			document
				?.querySelector('meta[name="description"]')
				.setAttribute('content', description || process.env.REACT_APP_META_DESC || '');
		});

		const { user } = useAppSelector((state) => state.auth);
		const dispatch = useAppDispatch();
		const navigate = useNavigate();

		// verify if the logged in user is actually who they claim to be on every protected page
		const verifyUser = async (retryCount = 0) => {
			try {
				const data = await authAPI.verify(user.token);
				if (data.success) {
					dispatch(
						loginUser({
							...data.data,
							token: user.token,
						}),
					);
				} else {
					// Use centralized error handler
					const shouldLogout = handleApiError({
						error: data,
						dispatch,
						navigate,
						darkModeStatus,
						showToast: retryCount > 0, // Only show toast after retry
					});

					// Retry once for network errors
					if (!shouldLogout && data.isNetworkError && retryCount === 0) {
						setTimeout(() => verifyUser(1), 2000); // Retry after 2 seconds
					}
				}
			} catch (error) {
				console.error('User verification failed:', error);
				// For unexpected errors, retry once then show warning
				if (retryCount === 0) {
					setTimeout(() => verifyUser(1), 2000);
					return;
				}
				
				toast('Unable to verify session. Please check your connection.', {
					type: 'warning',
					position: 'top-right',
					theme: darkModeStatus ? 'dark' : 'light',
				});
			}
		};

		useEffect(() => {
			const checkAdminAccess = async () => {
				if (isProtected && (!user || !user.token)) {
					navigate(`../${demoPagesMenu.login.path}`, { replace: true });
					return;
				}

				if (isProtected && user?.role?.roleName) {
					const hasAdminAccess = await isAdminRole(user.role?.roleName);
					if (!hasAdminAccess) {
						navigate(`../${demoPagesMenu.login.path}`, { replace: true });
						return;
					}
				}

				if (user && user.token) {
					verifyUser();
				}
			};

			checkAdminAccess();
		}, [user, isProtected]);

		return (
			<div ref={ref} className={classNames('page-wrapper', 'container-fluid', className)}>
				{children}
			</div>
		);
	},
);
PageWrapper.displayName = 'PageWrapper';
PageWrapper.propTypes = {
	isProtected: PropTypes.bool,
	title: PropTypes.string,
	description: PropTypes.string,
	// @ts-ignore
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
PageWrapper.defaultProps = {
	isProtected: true,
	title: undefined,
	description: undefined,
	className: undefined,
};

export default PageWrapper;
